import { CompetenzaFattureOutpuDto, GetListFattureAssociateUtenteEsternoRequest } from '@api/client';
import React, { useEffect, useState } from 'react';
import { Modal, Table } from 'react-bootstrap';
import LinksButton from 'shared/design-system/components/links-button';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'core/store/hooks';
import { getFattureRicevuteExternalCollaborators } from 'pages/private/active-cycle/active-cycle-bills/_redux/actions';
import {
  associateExternalCollaboratorsForInvoices,
  getCompetenzeMeseAnno,
  getExternalCollaboratorsForInvoices,
} from 'pages/private/passive-cycle/_redux/actions';
import { formatCurrency, getFormattedDateHeader, sortByMonthAndYear } from 'shared/utils/common.utils';
import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  idOrdine: number | undefined;
  currentInvoiceId?: string | undefined;
  editCollaborators?: boolean;
}

const AssociateExternalCollaboratorsToPassiveInvoiceModal: React.FC<Props> = ({
  isOpen,
  onClose,
  idOrdine,
  currentInvoiceId,
  editCollaborators,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [externalCollabs, setExternalCollabs] = useState<CompetenzaFattureOutpuDto[] | undefined>([]);

  useEffect(() => {
    if (!idOrdine) return;
    if (currentInvoiceId) {
      const payload: GetListFattureAssociateUtenteEsternoRequest = {
        fkRowFattura: currentInvoiceId,
        idOrdine,
      };
      dispatch(getExternalCollaboratorsForInvoices(payload))
        .unwrap()
        .then((res) => {
          if (res && res.length > 0) {
            setExternalCollabs(
              res.map((user) => ({
                ...user,
                fkRowFattura: currentInvoiceId,
                idOrdine,
                listAllocation: sortByMonthAndYear([...(user.listAllocation ?? [])]),
              }))
            );
          }
        });
    } else {
      dispatch(getCompetenzeMeseAnno(idOrdine))
        .unwrap()
        .then((res) => {
          if (res && res.length > 0) {
            setExternalCollabs(
              res.map((user) => ({
                ...user,
                fkRowFattura: currentInvoiceId,
                idOrdine,
                listAllocation: sortByMonthAndYear([...(user.listAllocation ?? [])]),
              }))
            );
          }
        });
    }
  }, [currentInvoiceId, idOrdine, dispatch]);

  const saveAssociation = () => {
    dispatch(associateExternalCollaboratorsForInvoices(externalCollabs || []))
      .unwrap()
      .then((res) => {
        if (res) {
          if (idOrdine) dispatch(getFattureRicevuteExternalCollaborators(idOrdine));
          onClose();
        }
      });
  };

  const handleChangeCompetenza = (
    e: CheckboxChangeEvent,
    month: number | undefined,
    year: number | undefined,
    idUser: number | undefined
  ) => {
    const { checked } = e.target;
    const updatedAllocations = externalCollabs?.map((user) => ({
      ...user,
      listAllocation: user.listAllocation?.map((allocation) => {
        if (allocation.anno === year && allocation.mese === month && user.idUtenteIntranet === idUser) {
          return { ...allocation, competenza: checked };
        }
        return allocation;
      }),
    }));
    setExternalCollabs(updatedAllocations);
  };

  const AllocationTable: React.FC<{ allocations: CompetenzaFattureOutpuDto[] | undefined }> = ({ allocations }) => {
    const allMonths = Array.from(
      new Set(allocations?.flatMap((user) => user.listAllocation?.map(({ mese, anno }) => `${anno}-${mese}`)))
    );

    return (
      <Table bordered>
        <thead>
          <tr>
            <th rowSpan={2}>Nominativo</th>
            <th className="text-center bg-disabled" colSpan={3}>
              Tot
            </th>
            {allMonths.map((key) => {
              const [year, month] = key?.split('-') || [];
              return (
                <th key={`${key}-label`} className="text-center" colSpan={2}>
                  {getFormattedDateHeader(parseInt(month), parseInt(year))}
                </th>
              );
            })}
          </tr>
          <tr>
            <th className="bg-disabled">A. M/D</th>
            <th className="bg-disabled">Rate</th>
            <th className="bg-disabled">A. Costs</th>
            {allMonths.map((key) => (
              <React.Fragment key={`${key}-sub`}>
                <th>A.M/D</th>
                <th>A.Costs</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {allocations?.map((user, rowIndex) => {
            const allocMap = Object.fromEntries(user.listAllocation?.map((a) => [`${a.anno}-${a.mese}`, a]) || []);
            const totAgg = user.listAllocation?.reduce((sum, a) => sum + (a.agg ?? 0), 0);
            const totCost = user.listAllocation?.reduce((sum, a) => sum + (a.acosti ?? 0) * (a.agg ?? 0), 0);
            const totCostFatture = user.listAllocation?.reduce((sum, a) => sum + (a.totImportoFatture ?? 0), 0);

            return (
              <React.Fragment key={user.idUtenteIntranet}>
                <tr>
                  <td className={rowIndex % 2 === 0 ? 'bg-light-blue' : ''}>{user.nominativo}</td>
                  <td className="bg-disabled">{formatCurrency(totAgg)}</td>
                  <td className="bg-disabled">{formatCurrency(user.rate)}€</td>
                  <td className="bg-disabled">{formatCurrency(totCost)}€</td>
                  {allMonths.map((key) => {
                    const alloc = key ? allocMap[key] : undefined;
                    return (
                      <React.Fragment key={`${user.idUtenteIntranet}-${key}`}>
                        <td className={rowIndex % 2 === 0 ? 'bg-light-blue' : ''}>
                          {alloc?.agg !== undefined ? formatCurrency(alloc?.agg) : '-'}
                        </td>
                        <td className={rowIndex % 2 === 0 ? 'bg-light-blue' : ''}>
                          {alloc?.acosti !== undefined ? `${formatCurrency(alloc.acosti * (alloc.agg ?? 0))}€` : '-'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
                <tr>
                  <td className={rowIndex % 2 === 0 ? 'bg-light-blue' : ''}>Competence</td>
                  <td className="bg-disabled" colSpan={3} />
                  {allMonths.map((key) => {
                    const alloc = key ? allocMap[key] : undefined;
                    return (
                      <td
                        className={`${rowIndex % 2 === 0 ? 'bg-light-blue' : ''} text-center`}
                        key={`${user.idUtenteIntranet}-${key}-comp`}
                        colSpan={2}
                      >
                        <Checkbox
                          checked={alloc?.competenza}
                          onChange={(e) => handleChangeCompetenza(e, alloc?.mese, alloc?.anno, user.idUtenteIntranet)}
                          disabled={!currentInvoiceId}
                        />
                      </td>
                    );
                  })}
                </tr>
                {!currentInvoiceId && (
                  <tr>
                    <td className={rowIndex % 2 === 0 ? 'bg-light-blue' : ''}>Tot amount invoices</td>
                    <td className="bg-disabled text-center" colSpan={3}>
                      {`${formatCurrency(totCostFatture)}€`}
                    </td>
                    {allMonths.map((key) => {
                      const alloc = key ? allocMap[key] : undefined;
                      return (
                        <td
                          className={`${rowIndex % 2 === 0 ? 'bg-light-blue' : ''} text-center`}
                          key={`${user.idUtenteIntranet}-${key}-tot`}
                          colSpan={2}
                        >
                          {alloc?.totImportoFatture !== undefined
                            ? `${formatCurrency(alloc?.totImportoFatture)}€`
                            : '-'}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{`${editCollaborators ? 'Update' : 'Associate'} External Collaborators to Invoice`}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ height: '75vh', maxHeight: '75vh', overflowY: 'auto' }}>
        <AllocationTable allocations={externalCollabs} />
      </Modal.Body>
      {currentInvoiceId && (
        <Modal.Footer>
          <div className="d-flex justify-content-end">
            <LinksButton className="btn btn-links-outline" onClick={onClose}>
              {t('common.cancel')}
            </LinksButton>
            <LinksButton
              className="btn-primary btn-links"
              onClick={saveAssociation}
              isDisabled={
                !externalCollabs?.length ||
                (!externalCollabs.some((user) => user.listAllocation?.some((alloc) => alloc.competenza)) &&
                  !editCollaborators)
              }
            >
              {editCollaborators ? t('common.update') : t('common.associate')}
            </LinksButton>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default AssociateExternalCollaboratorsToPassiveInvoiceModal;
